/**
 * @file driverPaperworkRecipients.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description Who each driver's paperwork pack goes to — the send window's
 *              list, shared by the Transport (bulk) and Bag boards, which each
 *              had their own copy of it.
 *
 *              One row per DRIVER (rows with no driver fall back to a haulier,
 *              else "Unassigned"); its address is the driver's own (Transport
 *              Settings → Drivers), else — the FALLBACK — their haulier's. The
 *              fallback is said on the row and summarised at the top, because
 *              for our own lorries it is the Excel Agri haulier's address,
 *              admin@excel-agri.com: the office then forwards the pack by hand,
 *              and forwarded mail is refused by iCloud and others (Cam, 14 Oct
 *              2026 — tobybond7@icloud.com bounced). Our mail is signed for
 *              excel-agri.com and sent through Amazon SES; forwarded on from
 *              the IONOS mailbox the sender no longer lines up (SPF, DKIM).
 *              The cure is the driver's own address, which the send window can
 *              save (PUT /api/transport/drivers/:id/email).
 *
 *              Group keys must match the server's (routes/transport.js
 *              generate-paperwork, groupKeyOf).
 */

import { API_URL } from '../config';

const clean = (v) => String(v == null ? '' : v).trim();

/**
 * @param {Array} rows                the day's paperwork rows (driverPaperworkRows(ourDeliveryRows(…)))
 * @param {Array} drivers             transport.drivers ({ id, name, email })
 * @param {Array} hauliersWithEmail   hauliers ({ id, name, email })
 */
export function buildDriverPaperworkRecipients(rows, drivers = [], hauliersWithEmail = []) {
  const groups = {};
  (rows || []).forEach(order => {
    const driver = clean(order.driver);
    const isDriver = !!driver;
    const name = isDriver ? driver : (clean(order.haulier) || 'Unassigned');
    const key = isDriver ? `driver:${driver.toLowerCase()}` : `haulier:${name.toLowerCase()}`;
    if (!groups[key]) groups[key] = { key, name, isDriver, haulier: clean(order.haulier), orderCount: 0 };
    groups[key].orderCount += 1;
  });

  const list = Object.values(groups).map(g => {
    let email = '';
    let ownEmail = '';
    let fallback = null;
    let driverId = null;
    let haulierId = null;
    if (g.isDriver) {
      const driverRecord = (drivers || []).find(d => d.name === g.name);
      driverId = driverRecord?.id || null;
      ownEmail = clean(driverRecord?.email);
      email = ownEmail;
      if (!email && g.haulier) {
        const h = (hauliersWithEmail || []).find(x => x.name === g.haulier);
        if (clean(h?.email)) {
          email = clean(h.email);
          fallback = { haulier: g.haulier, email };
        }
      }
    } else {
      const h = (hauliersWithEmail || []).find(x => x.name === g.name);
      haulierId = h?.id || null;
      email = clean(h?.email);
    }
    return {
      key: g.key,
      name: g.name,
      isDriver: g.isDriver,
      driverId,
      haulierId,
      haulier: g.haulier,
      // "Toby Bond (Excel Agri)" for a driver with a known haulier, else the name.
      label: g.isDriver && g.haulier ? `${g.name} (${g.haulier})` : g.name,
      orderCount: g.orderCount,
      email,
      ownEmail,
      // Set when the pack goes to the haulier's address because the driver has none.
      fallback,
      // A driver on record may have their own address typed in the send window.
      canAddEmail: g.isDriver && !!driverId && !ownEmail,
      emails: email ? [email] : [],
      settingsHint: g.isDriver ? 'Transport Settings → Drivers' : 'Transport Settings → Hauliers',
      include: !!email
    };
  });

  list.sort((a, b) => a.label.localeCompare(b.label));
  return list;
}

/** The row's sentence when its pack goes to the haulier's address. */
export function fallbackNote(r) {
  if (!r?.fallback) return null;
  return `No email for ${r.name} — this goes to ${r.fallback.haulier}'s ${r.fallback.email}. `
    + 'Add theirs in Transport Settings → Drivers (or here) so it reaches them directly.';
}

/**
 * The window's warning: how many switched-on packs go to a haulier's address
 * instead of the driver. Nothing when everything is going to the office anyway.
 */
export function fallbackWarning(recipients, { sendToOffice = false } = {}) {
  if (sendToOffice) return null;
  const falling = (recipients || []).filter(r => r.include && r.fallback);
  if (!falling.length) return null;
  const addresses = [...new Set(falling.map(r => r.fallback.email))].join(', ');
  const n = falling.length;
  return `${n} driver${n === 1 ? ' has' : 's have'} no email of their own, so ${n === 1 ? 'their pack goes' : 'their packs go'} `
    + `to their haulier's address (${addresses}) and would have to be forwarded — and forwarded paperwork is often `
    + 'refused by the driver\'s email (iCloud bounced it). Add each driver\'s own address so it reaches them directly.';
}

/**
 * Save a driver's own address from the send window (the same route Transport
 * Settings → Drivers uses; checked and audited there). Resolves with the
 * driver as saved; throws with the server's reason.
 */
export async function saveDriverEmail(driverId, email) {
  const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/email`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: clean(email), source: 'paperwork' })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || 'Could not save the address');
  return data.driver;
}

/** The list with one driver's own address saved: their pack now goes to them. */
export function withDriverEmail(recipients, key, email) {
  const e = clean(email);
  return (recipients || []).map(r => (r.key === key
    ? { ...r, email: e, ownEmail: e, emails: e ? [e] : [], fallback: null, canAddEmail: false, include: !!e }
    : r));
}
